/**
 * SupplyChainTimelinePanel — SCM4 Timeline tab.
 *
 * Flex-based Gantt rendering — deliberately simple instead of pulling in
 * a chart library. Each bar represents a single BoM item with width =
 * leadTimeMonthsHigh and position = `latestOrderDate → expectedOnSiteDate`.
 * Rows highlight in error/warning tones when the critical-path analysis
 * flags the item as overdue / at-risk.
 *
 * X-axis: a monotone timeline from the earliest order date (or today)
 * through the COD target. Years are rendered as tick labels. The COD
 * target itself is drawn as a vertical marker.
 *
 * MVP cap: at most 30 items shown — the spec calls for richer
 * deeplinking and per-bar tooltips, which we leave for a future polish
 * commit. The headline experience (sees the long-pole items overlap with
 * the COD window) lands here.
 */
import React, { useMemo } from 'react';
import { Clock } from 'lucide-react';
import type { BomItem, SupplyChainPlan } from '../../../types/supplyChain';
import { SCM_CATEGORY_LABELS } from '../../../types/supplyChain';
import { computeCriticalPath } from '../../../lib/criticalPathAnalysis';
import { cn } from '../../../lib/utils';

export interface SupplyChainTimelinePanelProps {
  plan: SupplyChainPlan | undefined;
  inServiceDate: string | null;
}

const MS_PER_MONTH = (365.25 / 12) * 24 * 60 * 60 * 1000;
const MAX_ROWS = 30;

function isCriticalPathLike(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  const normalized = value.trim().toLowerCase().replace(/[_\s]+/g, '-');
  return normalized === 'critical-path';
}

interface GanttRow {
  item: BomItem;
  /** ISO. Latest the order can be placed without slipping COD. */
  orderBy: string;
  /** ISO. Expected on-site date = orderBy + leadTimeHigh. */
  expectedOnSite: string;
  isOverdue: boolean;
  isOnCriticalPath: boolean;
}

function buildRows(plan: SupplyChainPlan, inServiceDate: string): GanttRow[] {
  const cp = computeCriticalPath(plan.bom, inServiceDate);
  const cpById = new Map(cp.items.map((i) => [i.bomItemId, i]));
  const cod = new Date(inServiceDate).getTime();
  const rows: GanttRow[] = plan.bom.map((item) => {
    const meta = cpById.get(item.id);
    const orderByMs = cod - item.leadTimeMonthsHigh * MS_PER_MONTH;
    const onSiteMs = cod;
    return {
      item,
      orderBy: new Date(orderByMs).toISOString(),
      expectedOnSite: new Date(onSiteMs).toISOString(),
      isOverdue: meta?.isOverdue ?? false,
      isOnCriticalPath: meta?.isOnCriticalPath ?? false,
    };
  });
  // Sort most-critical first so the eye lands on the long poles.
  rows.sort((a, b) => {
    if (a.isOverdue !== b.isOverdue) return a.isOverdue ? -1 : 1;
    if (a.isOnCriticalPath !== b.isOnCriticalPath) return a.isOnCriticalPath ? -1 : 1;
    return b.item.leadTimeMonthsHigh - a.item.leadTimeMonthsHigh;
  });
  return rows.slice(0, MAX_ROWS);
}

function yearTicks(startMs: number, endMs: number): number[] {
  const startYear = new Date(startMs).getUTCFullYear();
  const endYear = new Date(endMs).getUTCFullYear();
  const ticks: number[] = [];
  for (let y = startYear; y <= endYear; y++) {
    ticks.push(Date.UTC(y, 0, 1));
  }
  return ticks;
}

export const SupplyChainTimelinePanel: React.FC<SupplyChainTimelinePanelProps> = ({
  plan,
  inServiceDate,
}) => {
  const data = useMemo(() => {
    if (!plan || plan.bom.length === 0 || !inServiceDate) return null;
    const rows = buildRows(plan, inServiceDate);
    if (rows.length === 0) return null;
    const now = Date.now();
    const cod = new Date(inServiceDate).getTime();
    // Window = earliest orderBy (or today) → COD + 3 months trailing
    // so the COD marker isn't pinned against the right edge.
    let earliest = cod;
    for (const r of rows) {
      const t = new Date(r.orderBy).getTime();
      if (t < earliest) earliest = t;
    }
    const start = Math.min(earliest, now);
    const end = cod + 3 * MS_PER_MONTH;
    const span = end - start;
    if (span <= 0) return null;
    return { rows, start, end, span, cod, now };
  }, [plan, inServiceDate]);

  if (!plan || plan.bom.length === 0) {
    return (
      <div className="text-center py-8 space-y-3 border border-dashed border-border" data-testid="scm-timeline-empty">
        <Clock className="w-10 h-10 text-text-secondary mx-auto" />
        <div className="text-[10px] font-bold text-text-muted uppercase tracking-widest">
          Timeline
        </div>
        <p className="type-body text-text-secondary max-w-md mx-auto">
          Seed a BoM on the Overview tab — lead-time bars will appear here once
          items exist.
        </p>
      </div>
    );
  }

  if (!inServiceDate) {
    return (
      <div className="text-center py-8 space-y-3 border border-dashed border-border" data-testid="scm-timeline-no-cod">
        <Clock className="w-10 h-10 text-text-secondary mx-auto" />
        <div className="text-[10px] font-bold text-text-muted uppercase tracking-widest">
          Timeline
        </div>
        <p className="type-body text-text-secondary max-w-md mx-auto">
          Enter the project in-service date (COD) in Project Intent or
          Financial Assumptions to render this risk timeline.
        </p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="type-body text-text-secondary text-center py-6 border border-dashed border-border" data-testid="scm-timeline-no-data">
        Timeline window could not be computed — check BoM lead times.
      </div>
    );
  }

  const { rows, start, end, span, cod, now } = data;
  const ticks = yearTicks(start, end);
  const codPct = ((cod - start) / span) * 100;
  const nowPct = ((now - start) / span) * 100;

  return (
    <div className="space-y-3" data-testid="scm-timeline-panel">
      <div className="text-[10px] font-bold text-text-muted uppercase tracking-widest">
        Timeline — {rows.length} items {rows.length === MAX_ROWS && plan.bom.length > MAX_ROWS && (
          <span className="type-body font-normal normal-case text-text-secondary">
            (showing top {MAX_ROWS} of {plan.bom.length})
          </span>
        )}
      </div>

      {/* Year tick strip + COD marker on a positioned overlay so the
          bars + ticks share the exact same coordinate space. */}
      <div className="relative border border-border bg-background" data-testid="scm-timeline-chart">
        <div className="relative h-5 border-b border-border bg-muted">
          {ticks.map((t) => {
            const pct = ((t - start) / span) * 100;
            if (pct < 0 || pct > 100) return null;
            return (
              <div
                key={t}
                className="absolute top-0 bottom-0 border-l border-border pl-1 type-body text-text-secondary"
                style={{ left: `${pct}%` }}
              >
                {new Date(t).getUTCFullYear()}
              </div>
            );
          })}
          {/* "Today" marker — dashed vertical line. */}
          {nowPct >= 0 && nowPct <= 100 && (
            <div
              className="absolute top-0 bottom-0 border-l border-dashed border-text-secondary"
              style={{ left: `${nowPct}%` }}
              aria-label="today"
              data-testid="scm-timeline-now"
            />
          )}
          {/* COD marker — solid accent vertical. */}
          {codPct >= 0 && codPct <= 100 && (
            <div
              className="absolute top-0 bottom-0 border-l-2 border-primary"
              style={{ left: `${codPct}%` }}
              aria-label="COD"
              data-testid="scm-timeline-cod"
            >
              <span className="absolute -top-4 left-1 type-meta font-bold uppercase tracking-widest text-primary">
                COD
              </span>
            </div>
          )}
        </div>

        <ul className="divide-y divide-border" role="list">
          {rows.map((row) => {
            const startMs = new Date(row.orderBy).getTime();
            const endMs = new Date(row.expectedOnSite).getTime();
            const leftPct = ((startMs - start) / span) * 100;
            const widthPct = ((endMs - startMs) / span) * 100;
            const tone = row.isOverdue
              ? 'bg-error/70'
              : (row.isOnCriticalPath || isCriticalPathLike(row.item.criticality))
                ? 'bg-warning/70'
                : 'bg-primary/60';
            return (
              <li
                key={row.item.id}
                className="relative grid grid-cols-[10rem_1fr] gap-2 items-center px-2 py-2"
                data-testid={`scm-timeline-row-${row.item.id}`}
              >
                <div className="min-w-0">
                  <div className="text-[10px] font-bold text-foreground truncate">
                    {row.item.name}
                  </div>
                  <div className="type-body text-text-secondary truncate">
                    {SCM_CATEGORY_LABELS[row.item.category]} · {row.item.leadTimeMonthsHigh} mo
                  </div>
                </div>
                <div className="relative h-4">
                  <div
                    className={cn('absolute top-0 h-full', tone)}
                    style={{
                      left: `${Math.max(0, leftPct)}%`,
                      width: `${Math.min(100 - Math.max(0, leftPct), Math.max(0.5, widthPct))}%`,
                    }}
                    title={`${row.item.name}: order by ${new Date(row.orderBy).toLocaleDateString(undefined, { timeZone: 'UTC' })}, on-site by ${new Date(row.expectedOnSite).toLocaleDateString(undefined, { timeZone: 'UTC' })}`}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="flex items-center gap-4 type-body text-text-secondary">
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-3 h-2 bg-error/70" /> Overdue
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-3 h-2 bg-warning/70" /> Critical path
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-3 h-2 bg-primary/60" /> Standard
        </span>
      </div>
    </div>
  );
};
